import React, { useEffect, useMemo, useState } from "react";
import {
  Text, Icon, Stack, DocumentCard, DefaultButton, TooltipHost, Persona, PersonaSize,
} from "@fluentui/react";
import { useSelector } from "react-redux";
import {
  BaselineSecurityHealthCustomerDetail,
  GoodStateHealthMetric,
  GoodStateHealthCategory,
} from "../../../../slices/BaselineSecurityHealth.slice";
import { selectGraphToken } from "../../../../slices/Auth.slice";
import { logEvent } from "../../../../helper/AppInsightsHelper";

interface BaselineSecurityHealthTenantDetailProps {
  tenant: BaselineSecurityHealthCustomerDetail;
  showBackLink?: boolean;
  onBack?: () => void;
}

interface RenderableCategory extends GoodStateHealthCategory {
  categoryId: string;
  accentColor: string;
  icon: string;
}

type RiskLevel = "High" | "Moderate" | "Low";

// Converts an incoming band value — the current "…Adoption" format returned by
// the Kusto function, or a legacy bare risk band — to the internal risk band.
// Returns null when the value is missing or unrecognized so callers render no
// band pill instead of falling back to a misleading default.
const toRiskBand = (raw: string | null | undefined): RiskLevel | null => {
  const s = (raw ?? "").trim().toLowerCase();
  if (!s) return null;
  // Adoption bands (current format) — the inverse of the risk band.
  if (s === "high adoption") return "Low";
  if (s === "moderate adoption") return "Moderate";
  if (s === "low adoption") return "High";
  // Legacy bare risk bands.
  if (s === "high") return "High";
  if (s === "moderate") return "Moderate";
  if (s === "low") return "Low";
  return null;
};

// =============================================================================
// Theme tokens
// =============================================================================
const FONT_BODY = 13;
const FONT_LABEL = 11;

// Security360 blue used for the accent strip along the top of each container card.
const STRIP_BLUE = "#0078D4";

const RISK_PALETTE: Record<RiskLevel, { color: string; bg: string; text: string }> = {
  Low: { color: "#73AA24", bg: "#EFF6E0", text: "#3B5E0F" },
  Moderate: { color: "#D0B232", bg: "#FBF5D3", text: "#6B5B00" },
  High: { color: "#D77440", bg: "#FDF3EE", text: "#7D3B13" },
};

// Neutral styling for a score whose band is missing/unknown — no adoption pill
// is rendered and the visuals fall back to grey instead of a coloured band.
const NEUTRAL_PALETTE = { color: "#979593", bg: "#F3F2F1", text: "#484644" };

// Adoption is the user-facing framing of the underlying risk band, and the two
// are inverses: High risk = Low Adoption, Moderate risk = Moderate Adoption,
// Low risk = High Adoption. Colors stay keyed by the risk band.
const RISK_TO_ADOPTION: Record<RiskLevel, RiskLevel> = {
  High: "Low",
  Moderate: "Moderate",
  Low: "High",
};
const adoptionLabel = (risk: RiskLevel): string => `${RISK_TO_ADOPTION[risk]} Adoption`;

// Format the tenant value display string. The API now returns a display-ready
// string (e.g. "95%", "EXISTS", "12"); we just fall back to an em-dash when
// the value is missing.
const formatTenantValue = (value: string): string => {
  const trimmed = (value ?? "").trim();
  return trimmed.length === 0 ? "\u2014" : trimmed;
};

type StatusKind = "met" | "atRisk" | "notApplicable";

const STATUS_PALETTE: Record<StatusKind, { accent: string; bg: string; softBg: string; text: string; pillLabel: string; icon: string }> = {
  met: { accent: "#3FA45B", bg: "#EAF6EE", softBg: "#F6FBF8", text: "#1E7A44", pillLabel: "High Adoption", icon: "SkypeCircleCheck" },
  atRisk: { accent: "#E08A93", bg: "#FCEEF0", softBg: "#FEFAFB", text: "#B4485A", pillLabel: "Low Adoption", icon: "AlertSolid" },
  notApplicable: { accent: "#5C5C5C", bg: "#D2D0CE", softBg: "#F3F2F1", text: "#3B3B3B", pillLabel: "Pending Partner Input", icon: "Blocked2Solid" },
};

const CATEGORY_VISUALS: Record<string, { accentColor: string; icon: string }> = {
  baseline: { accentColor: "#0078D4", icon: "Shield" },
  passkey: { accentColor: "#107C41", icon: "Fingerprint" },
  patching: { accentColor: "#5C2E91", icon: "Sync" },
  vulnerabilities: { accentColor: "#C4314B", icon: "Bug" },
  endpoint: { accentColor: "#0F6CBD", icon: "Settings" },
  protection: { accentColor: "#CA5010", icon: "Mail" },
  hygiene: { accentColor: "#4864A5", icon: "ShieldSolid" },
  dormantaccount: { accentColor: "#8C6C2F", icon: "Contact" },
  monitoring: { accentColor: "#038387", icon: "View" },
};

const FALLBACK_CATEGORY_VISUALS = [
  { accentColor: "#0078D4", icon: "Shield" },
  { accentColor: "#107C41", icon: "Fingerprint" },
  { accentColor: "#5C2E91", icon: "Sync" },
  { accentColor: "#C4314B", icon: "Bug" },
  { accentColor: "#CA5010", icon: "Mail" },
  { accentColor: "#038387", icon: "View" },
  { accentColor: "#8C6C2F", icon: "BulletedList" },
  { accentColor: "#4864A5", icon: "ShieldSolid" },
];

const normalizeCategoryToken = (value?: string | null): string =>
  (value ?? "").replace(/[^a-z0-9]/gi, "").toLowerCase();

// Preferred display order for the category panes, keyed by normalized category
// token. Categories are rendered in this order regardless of the column order
// the API returns them in. "Insider Risk and Data Monitoring" (monitoring) is
// intentionally placed right after "Email and Collaboration Protection"
// (protection). Any category not listed here keeps its API order and is
// appended after the known ones.
const CATEGORY_DISPLAY_ORDER = [
  "baseline",
  "passkey",
  "patching",
  "endpoint",
  "protection",
  "monitoring",
  "hygiene",
  "dormantaccount",
  "vulnerabilities",
];

const categoryOrderIndex = (category: GoodStateHealthCategory): number => {
  const key = normalizeCategoryToken(category.categoryKey);
  const name = normalizeCategoryToken(category.categoryName);
  const idx = CATEGORY_DISPLAY_ORDER.findIndex((token) => key === token || key.includes(token) || name.includes(token));
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

const getCategoryVisuals = (category: GoodStateHealthCategory, index: number): { accentColor: string; icon: string } => {
  const normalizedKey = normalizeCategoryToken(category.categoryKey);
  const normalizedName = normalizeCategoryToken(category.categoryName);
  const directMatch = CATEGORY_VISUALS[normalizedKey];
  if (directMatch) {
    return directMatch;
  }

  const inferredEntry = Object.entries(CATEGORY_VISUALS).find(([token]) =>
    normalizedKey.includes(token) || normalizedName.includes(token)
  );
  if (inferredEntry) {
    return inferredEntry[1];
  }

  return FALLBACK_CATEGORY_VISUALS[index % FALLBACK_CATEGORY_VISUALS.length];
};

const hasRenderableCategoryContent = (category?: GoodStateHealthCategory | null): category is GoodStateHealthCategory => {
  if (!category) {
    return false;
  }

  // Some tenants can return placeholder categories with only a key (for example,
  // "Baseline") but no display name, KPIs, or detail rows. Hide those sections.
  return Boolean(
    (category.categoryName ?? "").trim()
    || category.totalActiveKPI
    || category.totalMetStandard
    || category.scorePercent
    || (category.details ?? []).length
  );
};

const buildLegacyCategoryFallback = (tenant: BaselineSecurityHealthCustomerDetail): GoodStateHealthCategory[] => {
  const legacyCategories: GoodStateHealthCategory[] = [
    { ...tenant.baseline, categoryKey: tenant.baseline?.categoryKey || "Baseline" },
    { ...tenant.passkey, categoryKey: tenant.passkey?.categoryKey || "Passkey" },
    { ...tenant.patching, categoryKey: tenant.patching?.categoryKey || "Patching" },
    { ...tenant.vulnerabilities, categoryKey: tenant.vulnerabilities?.categoryKey || "Vulnerabilities" },
  ];

  return legacyCategories.filter(hasRenderableCategoryContent);
};

// Maps the status colour keyword returned by the Kusto function ("Green" |
// "Red" | "Grey") to the internal status bucket, so the card reuses the exact
// palette shades already defined above. Unknown/blank colours fall back to the
// neutral (not-applicable) bucket.
const colorToStatusKind = (color: string | null | undefined): StatusKind => {
  switch ((color ?? "").trim().toLowerCase()) {
    case "green":
      return "met";
    case "red":
      return "atRisk";
    default:
      return "notApplicable";
  }
};

// A metric is "deferred" (sorted to the end of the grid) when it is
// Pending Partner Input, or has no benchmark / actual value yet. Treats "-" and
// the em-dash placeholder as empty.
const isDeferredMetric = (m: GoodStateHealthMetric): boolean => {
  const isBlank = (v?: string) => {
    const t = (v ?? "").trim();
    return t === "" || t === "-" || t === "\u2014";
  };
  return colorToStatusKind(m.color) === "notApplicable" || isBlank(m.benchmark) || isBlank(m.tenantValue);
};

// =============================================================================
// DRI (Directly Responsible Individual)
// -----------------------------------------------------------------------------
// The API (Fn_S360_GetBaselineSecurityHealthCustomerMetrics) returns `dri` and
// `driEmail` per signal. Both may be comma-separated (multiple owners), aligned
// by position. The alias (text before "@") is used to fetch the profile photo
// from Microsoft Graph — the same approach used by the customer contacts screen.
// =============================================================================
interface DriContact {
  fullName: string;
  email: string;
  alias: string;
}

const parseDriContacts = (dri?: string, driEmail?: string): DriContact[] => {
  const names = (dri ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  const emails = (driEmail ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  const count = Math.max(names.length, emails.length);
  const contacts: DriContact[] = [];
  for (let i = 0; i < count; i++) {
    const email = emails[i] ?? "";
    const alias = email.includes("@") ? email.split("@")[0] : email;
    const fullName = names[i] || alias || "Unknown";
    contacts.push({ fullName, email, alias });
  }
  return contacts;
};

const getInitials = (fullName: string): string =>
  fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");

// Module-level cache so a given person's photo is only fetched from Graph once
// per session, even across multiple signals / tooltip re-opens.
const graphPhotoCache = new Map<string, string>();

// Fetches a 48x48 profile photo for the given email from Microsoft Graph and
// returns it as a data URL. Falls back to null (initials avatar) when the photo
// is unavailable or the token is missing.
const useGraphPhoto = (email: string): string | null => {
  const graphToken = useSelector(selectGraphToken);
  const [image, setImage] = useState<string | null>(() => graphPhotoCache.get(email) ?? null);

  useEffect(() => {
    if (!email || !graphToken) return;
    const cached = graphPhotoCache.get(email);
    if (cached) {
      setImage(cached);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(email)}/photos/48x48/$value`,
          { headers: { Authorization: `Bearer ${graphToken}` } }
        );
        if (!response.ok) return;
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          graphPhotoCache.set(email, dataUrl);
          if (!cancelled) setImage(dataUrl);
        };
        reader.readAsDataURL(blob);
      } catch {
        // Photo fetch failed — the Persona will render initials instead.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [email, graphToken]);

  return image;
};

// A single DRI person row: Graph avatar + name + email.
const DriPersonRow: React.FC<{ contact: DriContact; accent: string }> = ({ contact, accent }) => {
  const image = useGraphPhoto(contact.email);
  const initials = getInitials(contact.fullName);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <Persona
        text={contact.fullName}
        imageInitials={initials}
        imageUrl={image ?? undefined}
        size={PersonaSize.size40}
        hidePersonaDetails
        styles={{ root: { borderRadius: "50%", overflow: "hidden", boxShadow: `0 0 0 2px ${accent}22` } }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#201F1E", lineHeight: 1.25 }}>{contact.fullName}</div>
        {contact.email && (
          <a
            href={`mailto:${contact.email}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "#0F6CBD", textDecoration: "none", marginTop: 3 }}
          >
            <Icon iconName="Mail" styles={{ root: { fontSize: 11 } }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contact.email}</span>
          </a>
        )}
      </div>
    </div>
  );
};

// Rich metric info callout: the metric description text followed by the DRI
// (Directly Responsible Individual) block, rendered inside one tooltip.
const MetricInfoCallout: React.FC<{
  description?: string;
  contacts: DriContact[];
  accent: string;
  // When true, appends a note pointing to the ETA page for signals whose data
  // is not yet available.
  etaNote?: boolean;
  // Identifies the metric the ETA note belongs to, so the ETA link click can be
  // attributed to a specific signal in telemetry.
  etaMetricName?: string;
  etaMetricKPI?: string;
}> = ({ description, contacts, accent, etaNote, etaMetricName, etaMetricKPI }) => (
  <div style={{ width: 300, padding: "12px 14px 14px" }}>
    {description && (
      <div style={{ fontSize: 12.5, color: "#323130", lineHeight: 1.5, marginBottom: (contacts.length || etaNote) ? 12 : 0 }}>
        {description}
      </div>
    )}
    {contacts.length > 0 && (
      <div style={{ borderTop: description ? "1px solid #EDEBE9" : "none", paddingTop: description ? 10 : 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, color: "#8A8886", textTransform: "uppercase", marginBottom: 8 }}>
          {contacts.length > 1 ? "Directly Responsible Individuals" : "Directly Responsible Individual"}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {contacts.map((c, i) => (
            <DriPersonRow key={c.email || c.fullName || i} contact={c} accent={accent} />
          ))}
        </div>
      </div>
    )}
    {etaNote && (
      <div
        style={{
          borderTop: (description || contacts.length) ? "1px solid #EDEBE9" : "none",
          marginTop: (description || contacts.length) ? 12 : 0,
          paddingTop: (description || contacts.length) ? 10 : 0,
          fontSize: 12,
          color: "#605E5C",
          lineHeight: 1.5,
        }}
      >
        Want to know when this signal is expected to be available? Check the estimated timelines (ETAs) at{" "}
        <a
          href="https://aka.ms/bsh_etas"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            logEvent(
              "Baseline Security Health - ETA Link Clicked",
              "Customer Page",
              "BaselineSecurityHealthTenantDetail",
              { metricName: etaMetricName, metricKPI: etaMetricKPI }
            )
          }
          style={{ color: "#0F6CBD", fontWeight: 600 }}
        >
          aka.ms/bsh_etas
        </a>
        .
      </div>
    )}
  </div>
);

// =============================================================================
// Building blocks
// =============================================================================
// Circular donut score ring. Renders a two-layer SVG ring with the score value
// centered inside — a clear, at-a-glance visual for percentage scores.
const DonutScore: React.FC<{ score: number; color: string; size?: number; stroke?: number }> = ({
  score, color, size = 72, stroke = 9,
}) => {
  const clamped = Math.max(0, Math.min(100, score));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (clamped / 100) * circumference;
  return (
    <div style={{ position: "relative", width: size, height: size, flex: "0 0 auto" }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#EDEBE9" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
          style={{ transition: "stroke-dasharray 0.5s ease" }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <span style={{ fontSize: size * 0.3, fontWeight: 700, color, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
          {Math.round(clamped)}
          <span style={{ fontSize: size * 0.16, fontWeight: 600 }}>%</span>
        </span>
      </div>
    </div>
  );
};

// =============================================================================
// Metric card for all categories
// =============================================================================
const MetricCard: React.FC<{ metric: GoodStateHealthMetric }> = ({ metric: m }) => {
  const [hovered, setHovered] = useState(false);
  const kind = colorToStatusKind(m.color);
  const palette = STATUS_PALETTE[kind];
  const accent = palette.accent;
  const isNotApplicable = kind === "notApplicable";
  const tenantValueText = formatTenantValue(m.tenantValue);
  const metricTooltip = (m.tooltip ?? "").trim();
  const pillLabel = (m.status ?? "").trim() || palette.pillLabel;
  const driContacts = parseDriContacts(m.dri, m.driEmail);
  const hasInfo = Boolean(metricTooltip) || driContacts.length > 0 || isNotApplicable;
  const fastTrackTag = (m.fastTrackTargetTag ?? "").trim();

  // For Pending Partner Input, use gradient grey theme for the entire card
  const cardBorderColor = isNotApplicable ? "#8A8886" : accent;
  const cardBgColor = isNotApplicable ? "linear-gradient(135deg, #F0F0F0 0%, #E0E0E0 100%)" : "#FFFFFF";

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        border: "1px solid #EDEBE9",
        borderLeft: `3px solid ${cardBorderColor}`,
        borderRadius: 10,
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        background: hovered ? (isNotApplicable ? "linear-gradient(135deg, #E8E8E8 0%, #D8D8D8 100%)" : `${accent}06`) : cardBgColor,
        boxSizing: "border-box",
        boxShadow: hovered
          ? `0 6px 16px ${isNotApplicable ? "rgba(0,0,0,.12)" : `${accent}25`}`
          : "0 1px 3px rgba(16,24,40,.06)",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        transition: "all .2s ease-out",
        cursor: "default",
      }}
    >
      {/* Header row: icon + name + info + status badge */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          <span
            style={{
              flex: "0 0 auto",
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: palette.bg,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon iconName={palette.icon} styles={{ root: { fontSize: 14, color: palette.text } }} />
          </span>
          <span style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#1A1A2E",
            lineHeight: 1.35,
          }}>
            {m.metricName}
          </span>
          {hasInfo && (
            <TooltipHost
              closeDelay={500}
              tooltipProps={{
                onRenderContent: () => (
                  <MetricInfoCallout description={metricTooltip} contacts={driContacts} accent={accent} etaNote={isNotApplicable} etaMetricName={m.metricName} etaMetricKPI={m.kpiId} />
                )
              }}
              calloutProps={{ gapSpace: 0, isBeakVisible: false, styles: { calloutMain: { borderRadius: 8, boxShadow: "0 6.4px 14.4px rgba(0,0,0,0.18)" } } }}
              styles={{ root: { display: "inline-flex" } }}
            >
              <Icon
                iconName="Info"
                aria-label={`About ${m.metricName}`}
                styles={{ root: { fontSize: 13, color: "#A19F9D", cursor: "help" } }}
              />
            </TooltipHost>
          )}
        </div>
        {/* Right-side badges: FastTrack tag + Status */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {fastTrackTag && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: "linear-gradient(135deg, #FFF4E5 0%, #FFE8C2 100%)",
                color: "#8A4B00",
                border: "1px solid #F5C17A",
                borderRadius: 999,
                padding: "4px 9px",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.01em",
                whiteSpace: "nowrap",
              }}
              title={fastTrackTag}
            >
              <Icon iconName="Flag" styles={{ root: { fontSize: 10, color: "#B85C00" } }} />
              {fastTrackTag}
            </span>
          )}
          <span
            style={{
              flex: "0 0 auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              backgroundColor: palette.bg,
              color: palette.text,
              padding: "4px 10px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            <Icon iconName={palette.icon} styles={{ root: { fontSize: 11 } }} />
            {pillLabel}
          </span>
        </div>
      </div>

      {/* Description */}
      {m.metricDescription && (
        <div style={{ fontSize: 12, color: "#605E5C", lineHeight: 1.5 }}>
          {m.metricDescription}
        </div>
      )}

      {/* Values row: benchmark vs actual with bordered boxes */}
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{
          flex: 1,
          minWidth: 0,
          backgroundColor: isNotApplicable ? "#EFEEEC" : "#F5F5F5",
          border: "1px solid #E1DFDD",
          borderRadius: 6,
          padding: "8px 12px",
        }}>
          <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: "#8A8886",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 3,
          }}>
            Benchmark
          </div>
          <div style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#323130",
          }}>
            {m.benchmark || "\u2014"}
          </div>
        </div>
        <div style={{
          flex: 1,
          minWidth: 0,
          backgroundColor: isNotApplicable ? "#EFEEEC" : "#F5F5F5",
          border: "1px solid #E1DFDD",
          borderRadius: 6,
          padding: "8px 12px",
        }}>
          <div style={{
            fontSize: 10,
            fontWeight: 700,
            color: "#8A8886",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 3,
          }}>
            Actual Value
          </div>
          <div style={{
            fontSize: 14,
            fontWeight: 700,
            color: isNotApplicable ? "#605E5C" : palette.text,
          }}>
            {tenantValueText}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Accordion Section Component
// =============================================================================
interface AccordionSectionProps {
  category: GoodStateHealthCategory;
  icon: string;
  accentColor: string;
  expanded: boolean;
  onToggle: () => void;
  fastTrackOnly?: boolean;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  category,
  icon,
  accentColor,
  expanded,
  onToggle,
  fastTrackOnly = false,
}) => {
  const risk = toRiskBand(category.riskStatus);
  const palette = risk ? RISK_PALETTE[risk] : NEUTRAL_PALETTE;

  const visibleMetrics = (category.details ?? []).filter((m) => {
    if (!fastTrackOnly) return true;
    return Boolean((m.fastTrackTargetTag ?? "").trim());
  });

  const sortedMetrics = [...visibleMetrics].sort((a, b) => {
    // Deferred metrics (Pending Partner Input / missing values) go to the end
    const aEnd = isDeferredMetric(a) ? 1 : 0;
    const bEnd = isDeferredMetric(b) ? 1 : 0;
    if (aEnd !== bEnd) return aEnd - bEnd;
    // Then sort by orderMetric if available
    const ao = a.orderMetric ?? Number.MAX_SAFE_INTEGER;
    const bo = b.orderMetric ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    // Fallback to alphabetical
    return (a.metricName ?? "").localeCompare(b.metricName ?? "");
  });

  return (
    <div
      style={{
        border: "1px solid #E1E5EB",
        borderRadius: 10,
        backgroundColor: "#FFFFFF",
        boxShadow: expanded ? `0 6px 22px ${accentColor}1E` : "0 1px 4px rgba(16,24,40,.08)",
        transition: "box-shadow 0.25s",
        overflow: "hidden",
      }}
    >
      {/* Accordion Header */}
      <button
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "14px 18px",
          border: "none",
          borderLeft: `4px solid ${accentColor}`,
          backgroundColor: expanded ? "#F8F8F8" : "#FFFFFF",
          cursor: "pointer",
          transition: "background-color 0.15s",
          textAlign: "left",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#F6F6F6";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = expanded ? "#F8F8F8" : "#FFFFFF";
        }}
      >
        {/* Icon */}
        <span
          style={{
            flex: "0 0 auto",
            width: 38,
            height: 38,
            borderRadius: 10,
            backgroundColor: `${accentColor}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon iconName={icon} styles={{ root: { fontSize: 18, color: accentColor } }} />
        </span>

        {/* Title & Subtitle */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14,
            fontWeight: 700,
            color: "#201F1E",
            lineHeight: 1.3,
            marginBottom: 2,
          }}>
            {category.categoryName}
          </div>
          <div style={{
            fontSize: 11,
            color: "#605E5C",
            lineHeight: 1.3,
          }}>
            {category.totalMetStandard} of {category.totalActiveKPI} metrics meet standard
          </div>
        </div>

        {/* Right: score ring + adoption badge (collapsed) + chevron */}
        <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 10 }}>
          {!expanded && risk && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
              color: palette.text, backgroundColor: palette.bg,
              padding: "3px 10px", borderRadius: 999,
              border: `1px solid ${palette.color}30`,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: palette.color }} />
              {adoptionLabel(risk)}
            </span>
          )}
          <DonutScore score={category.scorePercent ?? 0} color={accentColor} size={44} stroke={5} />
          <Icon
            iconName={expanded ? "ChevronUp" : "ChevronDown"}
            styles={{ root: { fontSize: 14, color: "#605E5C", transition: "transform 0.2s" } }}
          />
        </div>
      </button>

      {/* Accordion Body */}
      <div
        style={{
          display: expanded ? "block" : "none",
        }}
      >
        <div style={{ padding: "0 18px 18px" }}>
          {/* Progress bar + score summary */}
          <div style={{ marginBottom: 16, paddingTop: 4 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: "#605E5C", fontWeight: 500 }}>
                {category.totalMetStandard} of {category.totalActiveKPI} KPIs meet standard
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: palette.text }}>
                {Math.round(category.scorePercent ?? 0)}%
              </span>
            </div>
            <div style={{ height: 7, borderRadius: 999, backgroundColor: "#EDEBE9", overflow: "hidden" }}>
              <div style={{
                height: "100%",
                width: `${Math.max(0, Math.min(100, category.scorePercent ?? 0))}%`,
                background: `linear-gradient(90deg, ${palette.color}88, ${palette.color})`,
                borderRadius: 999,
                transition: "width 0.4s",
              }} />
            </div>
          </div>

          {/* Metrics Grid - 2 columns */}
          {sortedMetrics.length === 0 ? (
            <div style={{ fontSize: FONT_BODY, color: "#605E5C", paddingTop: 4 }}>
              No metrics available for this category.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 16,
              }}
            >
              {sortedMetrics.map((m, idx) => (
                <MetricCard key={`${m.kpiId || m.metricName}-${idx}`} metric={m} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div >
  );
};

// =============================================================================
// Main
// =============================================================================
export const BaselineSecurityHealthTenantDetail: React.FC<BaselineSecurityHealthTenantDetailProps> = ({
  tenant, showBackLink = true, onBack,
}) => {
  const overall = toRiskBand(tenant.overallRiskStatus);
  const overallPalette = overall ? RISK_PALETTE[overall] : NEUTRAL_PALETTE;

  const renderedCategories = useMemo<RenderableCategory[]>(() => {
    const sourceCategories = (tenant.categories ?? []).filter(hasRenderableCategoryContent);
    const categories = sourceCategories.length > 0 ? sourceCategories : buildLegacyCategoryFallback(tenant);

    // Apply the preferred display order while keeping a stable fallback (original
    // API order) for any categories not listed in CATEGORY_DISPLAY_ORDER.
    const orderedCategories = categories
      .map((category, index) => ({ category, index }))
      .sort((a, b) => {
        const orderDelta = categoryOrderIndex(a.category) - categoryOrderIndex(b.category);
        return orderDelta !== 0 ? orderDelta : a.index - b.index;
      })
      .map(({ category }) => category);

    return orderedCategories.map((category, index) => {
      const { accentColor, icon } = getCategoryVisuals(category, index);
      const normalizedKey = normalizeCategoryToken(category.categoryKey);
      const normalizedName = normalizeCategoryToken(category.categoryName);

      return {
        ...category,
        categoryId: normalizedKey || normalizedName || `category-${index}`,
        accentColor,
        icon,
      };
    });
  }, [tenant]);

  // Track which accordion is open (only one at a time). All panes start
  // collapsed by default; the user expands a category on demand.
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [fastTrackOnly, setFastTrackOnly] = useState(false);

  useEffect(() => {
    // Only clear a stale reference when the open category no longer exists
    // (e.g. after the tenant/categories change). Never auto-open a pane so the
    // default state stays fully collapsed.
    if (openAccordion && !renderedCategories.some((category) => category.categoryId === openAccordion)) {
      setOpenAccordion(null);
    }
  }, [renderedCategories, openAccordion]);

  const fastTrackMetricCount = renderedCategories
    .flatMap((c) => c?.details ?? [])
    .filter((m) => Boolean((m.fastTrackTargetTag ?? "").trim()))
    .length;

  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { paddingBottom: 32 } }}>

      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} styles={{ root: { justifyContent: "space-between" } }}>
        {showBackLink ? (
          <DefaultButton
            iconProps={{ iconName: "ChevronLeft" }}
            text="All tenants"
            onClick={onBack}
            styles={{ root: { border: "none", padding: "0 4px", height: 28, minWidth: 0, boxShadow: "none" }, label: { fontSize: FONT_BODY, color: "#0078D4", fontWeight: 600 } }}
          />
        ) : <span />}
        <button
          onClick={() => setFastTrackOnly((v) => !v)}
          style={{
            border: fastTrackOnly ? "1px solid #B85C00" : "1px solid #C8C6C4",
            background: fastTrackOnly ? "linear-gradient(135deg, #FFF4E5 0%, #FFE8C2 100%)" : "#FFFFFF",
            color: fastTrackOnly ? "#8A4B00" : "#323130",
            borderRadius: 999,
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            boxShadow: fastTrackOnly ? "0 2px 8px rgba(184,92,0,.18)" : "none",
            transition: "all .18s ease",
          }}
          aria-pressed={fastTrackOnly}
          title="Show only FastTrack-targeted metrics"
        >
          <Icon iconName="Flag" styles={{ root: { fontSize: 12, color: fastTrackOnly ? "#B85C00" : "#605E5C" } }} />
          {fastTrackOnly ? "FastTrack Only: ON" : "FastTrack Only"}
          <span style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 20,
            height: 20,
            borderRadius: 999,
            padding: "0 6px",
            fontSize: 11,
            fontWeight: 700,
            backgroundColor: fastTrackOnly ? "#B85C00" : "#F3F2F1",
            color: fastTrackOnly ? "#FFFFFF" : "#605E5C",
          }}>
            {fastTrackMetricCount}
          </span>
        </button>
      </Stack>

      {/* ── Good State Health Summary ── */}
      <DocumentCard
        styles={{
          root: {
            maxWidth: "100%",
            padding: 0,
            borderRadius: 12,
            boxShadow: "0 4px 20px rgba(16,24,40,.14)",
            overflow: "hidden",
          },
        }}
      >
        {/* ── Hero header ── */}
        <div style={{ height: 4, background: "linear-gradient(90deg, #1E874B 0%, #D9B100 33%, #C86A00 66%, #C4314B 100%)" }} />
        <div style={{
          background: "#FFFFFF",
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 9,
              backgroundColor: "#F3F2F1",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Icon iconName="ShieldSolid" styles={{ root: { fontSize: 20, color: "#0F6CBD" } }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#323130", lineHeight: 1.2 }}>
                Ready for AI Security Health
              </div>
              <div style={{ fontSize: 11, color: "#605E5C", marginTop: 3 }}>
                Overall adoption across your Ready for AI initiatives. Grading: High Adoption ≥80% · Moderate 50–80% · Low &lt;50%.
              </div>
            </div>
          </Stack>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
            <TooltipHost
              content="Overall security health score based on the Ready for AI categories currently returned for this tenant. Higher scores indicate better security posture."
              styles={{ root: { display: "inline-flex" } }}
            >
              <Icon iconName="Info" styles={{ root: { fontSize: 14, color: "#605E5C", cursor: "help" } }} />
            </TooltipHost>
            {overall && (
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                backgroundColor: "#FDECF0",
                color: "#C4314B",
                padding: "5px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                border: "1px solid #F4B9C2",
              }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: overallPalette.color }} />
                {adoptionLabel(overall)}
              </div>
            )}
          </Stack>
        </div>

        {/* ── Main stats row ── */}
        <div style={{ padding: "20px 24px 16px", backgroundColor: "#FFFFFF" }}>
          <Stack horizontal tokens={{ childrenGap: 24 }} verticalAlign="center" wrap>
            {/* Left: Donut Score */}
            <DonutScore
              score={tenant.overallScorePercent}
              color={overallPalette.color}
              size={100}
              stroke={12}
            />
            {/* Right: Stats */}
            <Stack tokens={{ childrenGap: 6 }} styles={{ root: { flex: 1, minWidth: 200 } }}>
              <Text styles={{ root: { fontSize: 15, fontWeight: 700, color: "#201F1E" } }}>
                Overall Score: {Math.round(tenant.overallScorePercent)}%
              </Text>
              <Text styles={{ root: { fontSize: 13, color: "#605E5C", lineHeight: 1.4, marginBottom: 8 } }}>
                {tenant.overallMetStandard} of {tenant.overallActiveKPI} metrics meet standard across all categories
              </Text>
              <div style={{ height: 8, borderRadius: 999, backgroundColor: "#EDEBE9", overflow: "hidden", maxWidth: 320 }}>
                <div style={{
                  height: "100%",
                  width: `${Math.max(0, Math.min(100, tenant.overallScorePercent))}%`,
                  background: `linear-gradient(90deg, ${overallPalette.color}88, ${overallPalette.color})`,
                  borderRadius: 999, transition: "width 0.4s",
                }} />
              </div>
            </Stack>
          </Stack>
        </div>

        {/* mini tiles removed per UX request; accordion remains primary navigation */}
      </DocumentCard>

      {/* ── Category Accordion Sections ── */}
      <Stack tokens={{ childrenGap: 12 }}>
        {renderedCategories.map((category) => (
          <AccordionSection
            key={category.categoryId}
            category={category}
            icon={category.icon}
            accentColor={category.accentColor}
            expanded={openAccordion === category.categoryId}
            onToggle={() => setOpenAccordion(openAccordion === category.categoryId ? null : category.categoryId)}
            fastTrackOnly={fastTrackOnly}
          />
        ))}
      </Stack>

    </Stack>
  );
};
